// frontend/src/utils/languageUtils.ts
// Utility functions for language handling

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
  normalizeLanguageCode,
} from '../locales/languages';
import i18n from '../i18n';
import { API_CONFIG } from './config';

import logger from '../utils/logger';

/**
 * Gets the browser's preferred language
 */
export const getBrowserLanguage = (): string => {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;
  
  const browserLang = navigator.language
    || (navigator as Navigator & { userLanguage?: string }).userLanguage
    || DEFAULT_LANGUAGE;
  return normalizeLanguageCode(browserLang) || DEFAULT_LANGUAGE;
};

/**
 * Gets language from URL parameter
 */
export const getLanguageFromURL = (): string | null => {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  return normalizeLanguageCode(params.get('lang'));
};

/**
 * Sets language in URL without reloading
 */
export const setLanguageInURL = (lang: string): void => {
  if (typeof window === 'undefined') return;
  
  const url = new URL(window.location.href);
  const liveLanguage = normalizeLanguageCode(lang);
  if (liveLanguage) {
    url.searchParams.set('lang', liveLanguage);
  } else {
    url.searchParams.delete('lang');
  }
  window.history.pushState({}, '', url.toString());
};

/**
 * Removes language parameter from URL
 */
export const removeLanguageFromURL = (): void => {
  if (typeof window === 'undefined') return;
  
  const url = new URL(window.location.href);
  url.searchParams.delete('lang');
  window.history.pushState({}, '', url.toString());
};

/**
 * Detects if a language uses RTL (Right-to-Left) script
 * EN and FR both use LTR
 */
export const isRTLLanguage = (_langCode: string): boolean => {
  return false;
};

/**
 * Gets the display name for a language code
 */
export const getLanguageDisplayName = (code: string, inNativeLanguage: boolean = false): string => {
  const lang = getLanguageByCode(code) || SUPPORTED_LANGUAGES[0];
  
  return inNativeLanguage ? lang.nativeName : lang.name;
};

/**
 * Gets language flag emoji
 */
export const getLanguageFlag = (code: string): string => {
  const lang = getLanguageByCode(code);
  return lang?.flag || '🌐';
};

/**
 * Validates if a language code is supported
 */
export const isLanguageSupported = (code: string): boolean => {
  return normalizeLanguageCode(code) !== null;
};

const resolveLiveLanguage = (langCode?: string): string => {
  return normalizeLanguageCode(
    langCode || i18n.resolvedLanguage || i18n.language
  ) || DEFAULT_LANGUAGE;
};

/**
 * Gets list of supported languages
 */
export const getPopularLanguages = (): string[] => {
  return ['en', 'fr'];
};

/**
 * Formats a localized date string
 * Auto-detects current language if not provided
 */
export const formatLocalizedDate = (
  date: Date | string | number,
  langCode?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  try {
    const dateOnlyMatch = typeof date === 'string'
      ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
      : null;
    const dateOnlyCandidate = dateOnlyMatch
      ? new Date(
          Number(dateOnlyMatch[1]),
          Number(dateOnlyMatch[2]) - 1,
          Number(dateOnlyMatch[3]),
          12
        )
      : null;
    const hasValidDateOnlyParts = Boolean(
      dateOnlyMatch
      && dateOnlyCandidate
      && dateOnlyCandidate.getFullYear() === Number(dateOnlyMatch[1])
      && dateOnlyCandidate.getMonth() === Number(dateOnlyMatch[2]) - 1
      && dateOnlyCandidate.getDate() === Number(dateOnlyMatch[3])
    );
    const dateObj = hasValidDateOnlyParts && dateOnlyCandidate
      ? dateOnlyCandidate
      : typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;
    const lang = resolveLiveLanguage(langCode);
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    
    return new Intl.DateTimeFormat(lang, options || defaultOptions).format(dateObj);
  } catch (error) {
    return date.toString();
  }
};

/**
 * Formats an API month value (YYYY-MM) without crossing a month boundary in
 * time zones west of UTC.
 */
export const formatLocalizedMonth = (
  month: string,
  langCode?: string,
  options?: Intl.DateTimeFormatOptions
): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return month;

  return formatLocalizedDate(
    new Date(year, monthIndex, 1, 12),
    langCode,
    options || { year: 'numeric', month: 'long' }
  );
};

/**
 * Formats a short date (10/30/2025)
 */
export const formatShortDate = (date: Date | string | number, langCode?: string): string => {
  return formatLocalizedDate(date, langCode, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

/**
 * Formats a date with time
 */
export const formatDateTime = (date: Date | string | number, langCode?: string): string => {
  return formatLocalizedDate(date, langCode, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats a relative time ("2 hours ago")
 */
export const formatRelativeTime = (date: Date | string | number, langCode?: string): string => {
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    const lang = resolveLiveLanguage(langCode);
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    
    if (diffDay > 0) return rtf.format(-diffDay, 'day');
    if (diffHour > 0) return rtf.format(-diffHour, 'hour');
    if (diffMin > 0) return rtf.format(-diffMin, 'minute');
    return rtf.format(-diffSec, 'second');
  } catch (error) {
    return formatShortDate(date, langCode);
  }
};

/**
 * Formats a localized number
 * Auto-detects current language if not provided
 */
export const formatLocalizedNumber = (
  num: number,
  langCode?: string,
  options?: Intl.NumberFormatOptions
): string => {
  try {
    const lang = resolveLiveLanguage(langCode);
    return new Intl.NumberFormat(lang, options).format(num);
  } catch (error) {
    return num.toString();
  }
};

/**
 * Formats a compact number (1.2K, 1.5M)
 */
export const formatCompactNumber = (num: number, langCode?: string): string => {
  return formatLocalizedNumber(num, langCode, {
    notation: 'compact',
    compactDisplay: 'short',
  });
};

/**
 * Formats a percentage
 */
export const formatPercentage = (num: number, langCode?: string, decimals: number = 0): string => {
  return formatLocalizedNumber(num / 100, langCode, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a localized currency
 * Auto-detects current language if not provided
 */
export const formatLocalizedCurrency = (
  amount: number,
  currency: string = 'USD',
  langCode?: string
): string => {
  try {
    const lang = resolveLiveLanguage(langCode);
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount}`;
  }
};

/**
 * Formats a compact currency (€1.2K, $1.5M)
 */
export const formatCompactCurrency = (
  amount: number,
  currency: string = 'USD',
  langCode?: string
): string => {
  try {
    const lang = resolveLiveLanguage(langCode);
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
      notation: 'compact',
      compactDisplay: 'short',
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount}`;
  }
};

/**
 * Pluralization helper
 * Handles different plural forms based on language rules
 * 
 * Usage:
 *   pluralize(1, 'item', 'items') → "1 item"
 *   pluralize(5, 'item', 'items') → "5 items"
 *   pluralize(2, { one: 'élément', other: 'éléments' }, 'fr') → "2 éléments"
 */
export const pluralize = (
  count: number,
  forms: string | { zero?: string; one?: string; two?: string; few?: string; many?: string; other: string },
  langCode?: string
): string => {
  const lang = resolveLiveLanguage(langCode);
  
  // Simple string form (English-style: singular, plural)
  if (typeof forms === 'string') {
    return `${formatLocalizedNumber(count, lang)} ${count === 1 ? forms : forms + 's'}`;
  }
  
  // Advanced pluralization using Intl.PluralRules
  try {
    const pr = new Intl.PluralRules(lang);
    const rule = pr.select(count);
    const form = forms[rule] || forms.other;
    return `${formatLocalizedNumber(count, lang)} ${form}`;
  } catch (error) {
    const form = count === 1 ? (forms.one || forms.other) : forms.other;
    return `${count} ${form}`;
  }
};

/**
 * Gets the plural category for a number in a language
 * Useful for complex pluralization logic
 */
export const getPluralCategory = (count: number, langCode?: string): Intl.LDMLPluralRule => {
  try {
    const lang = resolveLiveLanguage(langCode);
    const pr = new Intl.PluralRules(lang);
    return pr.select(count);
  } catch (error) {
    return count === 1 ? 'one' : 'other';
  }
};

/**
 * Gets the appropriate locale for a language code
 */
export const getLocaleForLanguage = (langCode: string): string => {
  const localeMap: { [key: string]: string } = {
    en: 'en-US',
    fr: 'fr-FR',
  };
  
  return localeMap[normalizeLanguageCode(langCode) || DEFAULT_LANGUAGE] || 'en-US';
};

/**
 * Sends language preference to backend
 */
export const saveLanguagePreference = async (langCode: string): Promise<void> => {
  const liveLanguage = normalizeLanguageCode(langCode);
  if (!liveLanguage) {
    logger.warn('Ignored unsupported live language preference:', langCode);
    return;
  }

  try {
    const apiUrl = API_CONFIG.BACKEND_URL;
    const response = await fetch(`${apiUrl}/api/user/preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-lang': liveLanguage,
      },
      credentials: 'include',
      body: JSON.stringify({ language: liveLanguage }),
    });
    if (!response.ok) {
      logger.warn(`Language preference was not persisted (${response.status})`);
    }
  } catch (error) {
    logger.warn('Failed to save language preference:', error);
  }
};

/**
 * Preloads critical translations (EN/FR only)
 */
export const preloadCriticalTranslations = async (): Promise<void> => {
  // Translations are bundled with the app, no need to fetch
  return Promise.resolve();
};
